import { React, useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
    Button,
    Grid,
    Typography,
    TextField,
    IconButton,
    useTheme,
    useMediaQuery
} from "@mui/material"
import { Dialog, DialogContent, TablePagination, InputAdornment } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace"

import resetHeaders from "../../functions/resetHeaders"
import {
    getAllActiveEmployees,
    getAllEmployees,
    getAllISOUpdates
} from "../../actions/isoActions"
import useStyles, { BackButtonMainStyles, CreateNewButtonStyles, DocumentListItemStyles, DocumentUpdateListStyles, ISOPageTitleStyles, NoDocumentStyles, PageTitleStyles, SearchBarStyles, SwapSortIconStyles } from "./ISOStyles"
import LoginPrompt from "../login/LoginPrompt"
import ISOForm from "./ISOForm"
import DocumentListItem from "./DocumentListItem"

function ISODashboard() {
    const theme = useTheme()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const isoRefreshKey = useSelector((state) => state.iso.isoRefreshKey)
    const permission = useSelector((state) => state.auth.permissions)

    // list of all current ISO document updates
    const allDocumentUpdates = useSelector((state) => state.iso.allISOUpdates)

    // boolean flag for small screen size (mobile)
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))

    // state repr. for text in search box
    const [searchText, setSearchText] = useState("")

    // state repr. for reverse sort
    const [reordered, setReordered] = useState(true)

    // state repr. for table pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(5)

    // display list of document updates (descending date)
    const allDocumentUpdatesList = allDocumentUpdates.filter(
        (doc) =>
            !searchText ||
            doc.DocumentNumber.toLowerCase().includes(
                searchText.toLowerCase()
            ) ||
            doc.DocumentTitle.toLowerCase().includes(searchText.toLowerCase())
    )

    const allDocumentUpdatesListOrdered = allDocumentUpdatesList.length > 0 ? reordered
        ? allDocumentUpdatesList.sort(
              (a, b) =>
                  new Date(b.DateImplemented).getTime() -
                  new Date(a.DateImplemented).getTime()
          )
        : allDocumentUpdatesList.sort(
              (a, b) =>
                  new Date(a.DateImplemented).getTime() -
                  new Date(b.DateImplemented).getTime()
          )
        :
        []

    // state variable for form dialog
    const [openForm, setOpenForm] = useState(false)

    const openFormFxn = () => {
        setOpenForm(true)
    }

    const closeForm = () => {
        setOpenForm(false)
    }

    // Handler function for clear option in search bar
    const handleClear = () => {
        setSearchText("")
    }

    // Handler function for search bar textfield
    const handleChangeSearch = (e) => {
        setSearchText(e.target.value)
    }

    const handleOnClickReverseSort = () => {
        const flag = reordered
        setReordered(!flag)
        setPage(0)
    }

    const handleBackClick = () => {
        navigate(`/home`)
    }

    // Store Variables
    const loggedIn = useSelector(
        (state) => state.auth.user && state.auth.user.auth
    )
    const authenticated = useSelector((state) => state.auth.authenticated)

    useEffect(() => {
        if (loggedIn) {
            resetHeaders()
            async function runUseEffectFunctions() {
                await dispatch(getAllEmployees())
                await dispatch(getAllActiveEmployees()) 
                await dispatch(getAllISOUpdates())
            }
            runUseEffectFunctions()
        }
    }, [dispatch, loggedIn])

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            {authenticated ? (
                <div key={isoRefreshKey}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="center"
                        sx={PageTitleStyles}
                    >
                        <IconButton
                            color="inherit"
                            size="medium"
                            sx={BackButtonMainStyles}
                            onClick={handleBackClick}
                        >
                            <KeyboardBackspaceIcon />
                                <Typography>
                                    BACK
                                </Typography>
                        </IconButton>
                        <Typography sx={ISOPageTitleStyles}>
                            ISO REGULATORY UPDATES
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        align="center"
                        justifyContent="space-evenly"
                    >
                    
                        {/* search bar */}
                        
                            <Grid item size={12}>
                                {permission?.CreateISODocumentUpdate ? (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={openFormFxn}
                                    sx={CreateNewButtonStyles}
                                >
                                    New ISO Update
                                </Button>
                            ) : (
                                null
                            )}
                                <TextField
                                    style={{ marginLeft: "16px", width: "25%", maxWidth: "360px" }}
                                    placeholder="Search..."
                                    value={searchText}
                                    sx={SearchBarStyles}
                                    onChange={handleChangeSearch}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment
                                                disableTypography={true}
                                                position="start"
                                                style={{ color: "inherit" }}
                                            >
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <IconButton
                                                style={{ padding: "5px" }}
                                                onClick={handleClear}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                />
                            </Grid>
                        <Grid
                            container
                            item
                            size={{ xs: 10, md: 8 }}
                            justifyContent="center"
                            sx={DocumentUpdateListStyles}
                        >
                            {allDocumentUpdatesListOrdered.length > 0 ? (
                                <Grid
                                    item
                                    size={12}
                                    sx={SwapSortIconStyles}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "nowrap" }}
                                >
                                    <TablePagination
                                         rowsPerPageOptions={[5, 10, 25]}
                                        style={{color: "white"}}
                                        component="div"
                                        count={allDocumentUpdatesListOrdered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                    <IconButton
                                        onClick={handleOnClickReverseSort}
                                    >
                                        {reordered ? (
                                            <ArrowDownwardIcon />
                                        ) : (
                                            <ArrowUpwardIcon />
                                        )}
                                        <Typography>SORT</Typography>
                                    </IconButton>
                                </Grid>
                            ) : (
                                ""
                            )}
                            {allDocumentUpdatesListOrdered.length > 0 ? (
                                allDocumentUpdatesListOrdered.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((doc) => (
                                    <Grid item size={12}>
                                    <DocumentListItem
                                        key={doc.DocumentUpdateId}
                                        documentObj={doc}
                                    />
                                    </Grid>
                                ))
                            ) : (
                                <Grid
                                    item
                                    size={12}
                                    sx={DocumentListItemStyles}
                                >
                                    <Typography
                                        align="center"
                                        variant="h6"
                                        sx={NoDocumentStyles}
                                    >
                                        <i>No document updates to show.</i>
                                    </Typography>
                                </Grid>
                            )}
                            {/* if len > 0 map thru list of ISO updates; else no ISO updates */}
                        </Grid>
                        
                    </Grid>
                    <Dialog
                        open={openForm}
                        onClose={closeForm}
                        maxWidth="sm"
                        fullWidth={true}
                    >
                        <DialogContent sx={{ height: "570px" }}>
                            <ISOForm onSubmit={closeForm} />
                        </DialogContent>
                    </Dialog>
                </div>
            ) : (
                <LoginPrompt />
            )}
        </>
    )
}

export default ISODashboard
